<template>
  <v-app-bar
    :height="$vuetify.display.xs ? 56 : 76"
    class="first-top-bar px-xs-2  px-sm-5 px-md-10"
    flat absolute
  >
    <v-btn
      v-if="$vuetify.display.smAndDown"
      @click="toggleSidemenu()"
      class="mx-1"
      height="48px"
      width="48px"
      elevation=0
      size="large" icon tile
    >
      <v-icon class="mr-0" size="28px">mdi-view-split-horizontal</v-icon>
    </v-btn>

    <router-link v-bind:to="getHeaderLink" class="pod-main-logo mx-1">
      <img v-if="getDarkMode" src="../../../../assets/images/smalltw-dark.png"
           :alt="$t('menus.topBars.home')"/>
      <img v-if="!getDarkMode" src="../../../../assets/images/smalltw-light.png"
           :alt="$t('menus.topBars.home')"/>
    </router-link>

    <v-spacer/>

    <div v-if="pod" class="pod-name-wrapper mr-2">
      <div
        v-if="!isEditingName"
        class="top-pod-name text-truncate"
        :class="{ 'pod-name-hover': !isEditingName }"
        @click="startEditingName"
      >
        <v-icon
          v-if="pod.teamonly === 1"
          class="mr-1"
          size="small"
          :title="$t('routes.node.accessTypeTeamOnly')"
        >
          mdi-file-account
        </v-icon>
        <span>{{ pod.name }}</span>
        <v-icon class="edit-icon ml-1" size="small">mdi-pencil</v-icon>
      </div>

      <v-form v-else ref="renameForm" @submit.prevent="saveNameChange">
        <v-text-field
          v-model="editedName"
          :rules="podNameRules"
          :maxlength="podNameMaxLength"
          density="compact"
          variant="solo-filled"
          hide-details="auto"
          flat
          autofocus
          class="pod-name-input"
          @blur="handleBlur"
          @keydown.esc="cancelEditingName"
          autoocomplete="off"
        >
          <template v-if="podExtension" v-slot:append-inner>
            <span class="pod-name-extension text-medium-emphasis" aria-hidden="true">
              {{ podExtension }}
            </span>
          </template>
        </v-text-field>
      </v-form>
    </div>

    <v-menu
      v-if="getPodVersions.length > 1"
      open-on-hover
      open-on-click
      location="bottom"
      :disabled="isQuickSwitcherVisible"
    >
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="props"
          :size="$vuetify.display.smAndDown ? 'small' : undefined"
          color="primary"
          variant="flat"
          rounded="circle"
          elevation=0
          min-width="32"
          height="48"
          @click="maybeHideQuickSwitcher()"
          icon
        >
          <span>V.</span>
          <v-icon class="mr-0">mdi-menu-down</v-icon>
        </v-btn>
      </template>

      <v-list rounded="lg">
        <v-list-item
          v-for="(item, index) in getPodVersions"
          :key="index"
          :to="'/pod/'+item.id"
          role="listitem"
        >
          <v-list-item-title>{{ item.name }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-tooltip
      v-if="quickSwitcherPods.length > 1"
      open-delay="250"
      location="bottom"
      :aria-label="$t('routes.pod.topBar.quickFileSwitch')"
    >
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="props"
          elevation="0"
          :size="$vuetify.display.smAndDown ? 'small' : undefined"
          :aria-label="$t('routes.pod.topBar.quickFileSwitch')"
          min-width="32"
          width="50"
          rounded="circle"
          variant="tonal"
          @click="toggleQuickSwitcher"
          style="margin-left: 5px; border-radius: 50%;"
        >
          <v-icon size="medium" class="mr-0">mdi-view-carousel-outline</v-icon>
        </v-btn>
      </template>
      <span>{{ $t('routes.pod.topBar.quickFileSwitch') }}</span>
    </v-tooltip>

    <v-spacer/>

    <div v-if="showingUpgradeButton && $vuetify.display.mdAndUp" class="mr-2">
      {{ $t('layouts.main.subscriptionDaysLeft', getSubscriptionDaysLeft) }}
    </div>

    <v-btn
      v-if="showingUpgradeButton && $vuetify.display.mdAndUp"
      @click="openSubscriptionContactDialog"
      class="mr-2 bg-primary"
      elevation="0"
    >
      <v-icon>mdi-crown-circle</v-icon>
      {{ $t('layouts.main.upgradeSubscription') }}
    </v-btn>

    <DownloadsMenu />
    <NotificationsMenu />
    <BeeUserMenu :forceManagementConsole="true" />
  </v-app-bar>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useAuthStore } from '@/stores/AuthStore.js';
import { useMainStore } from '@/stores/MainStore.js';
import FilenameHelper from '@/components/utils/FilenameHelper.js';
import BeeUserMenu from './BeeUserMenu.vue';
import NotificationsMenu from './notifications/NotificationsMenu.vue';
import DownloadsMenu from './downloads/DownloadsMenu.vue';

export default {
  name: "PodsTopBar",
  components: {
    BeeUserMenu,
    NotificationsMenu,
    DownloadsMenu,
  },
  data() {
    return {
      quickSwitcherButtonPressed: false,
      isEditingName: false,
      editedName: '',
      podNameRules: [
        (v) => !!v || this.$t('routes.node.requiredName'),
        (v) => v && v.length <= this.podNameMaxLength || this.$t('routes.pod.fileNameTooLong'),
      ],
    };
  },
  beforeMount() {
    this.maybeHideQuickSwitcher();
  },
  methods: {
    openSubscriptionContactDialog() {
      const mainStore = useMainStore();
      mainStore.showSubscriptionContactDialog();
    },
    toggleSidemenu() {
      const mainStore = useMainStore();
      mainStore.toggleSidemenu();

      this.maybeHideQuickSwitcher();
    },
    maybeHideQuickSwitcher() {
      if (this.isQuickSwitcherVisible) {
        const podStore = usePodStore();
        podStore.toggleQuickSwitcher();
      }
    },
    toggleQuickSwitcher() {
      const podStore = usePodStore();
      podStore.toggleQuickSwitcher();
    },
    startEditingName() {
      if (!this.pod) {
        return;
      }

      this.isEditingName = true;
      this.editedName = this.podBaseName;
    },
    cancelEditingName() {
      this.isEditingName = false;
      this.editedName = '';
    },
    async handleBlur() {
      const trimmedName = this.editedName.trim();
      const fullName = FilenameHelper.preserveFilenameExtension(trimmedName, this.podExtension);

      if (!this.pod || fullName === this.pod.name) {
        this.cancelEditingName();
        return;
      }

      if (!this.$refs.renameForm) {
        this.cancelEditingName();
        return;
      }

      const validForm = await this.$refs.renameForm.validate();

      if (!validForm.valid) {
        this.cancelEditingName();
        return;
      }

      await this.saveNameChange();
    },
    async saveNameChange() {
      if (!this.$refs.renameForm) {
        return;
      }

      const validForm = await this.$refs.renameForm.validate();

      if (!validForm.valid) {
        return;
      }

      const trimmedName = this.editedName.trim();
      const fullName = FilenameHelper.preserveFilenameExtension(trimmedName, this.podExtension);

      if (fullName === this.pod.name) {
        this.cancelEditingName();
        return;
      }

      const data = {
        id: this.pod.id,
        name: trimmedName,
      };

      const podStore = usePodStore();

      try {
        await podStore.renameFile(data);
        this.cancelEditingName();

        if (this.$t && this.$showMessage) {
          this.$showMessage(this.$t('routes.pod.fileRenamed'), { type: 'success' });
        }
      } catch (error) {
        this.cancelEditingName();

        if (error?.response?.data?.error?.message) {
          this.$showMessage(error.response.data.error.message, { type: 'error' });
        } else {
          this.$showMessage('Error!', { type: 'error' });
        }

        console.log(error);
      }
    },
  },
  computed: {
    getRootNodeId() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    getHeaderLink() {
      if (this.getRootNodeId) {
        return '/node/' + this.getRootNodeId;
      }

      return '/';
    },
    getDarkMode() {
      const mainStore = useMainStore();
      return mainStore.getDarkMode;
    },
    pod() {
      const podStore = usePodStore();
      return podStore.getViewPod;
    },
    podBaseName() {
      return FilenameHelper.getFilenameBase(this.pod?.name || '');
    },
    podExtension() {
      return FilenameHelper.getFilenameExtension(this.pod?.name || '');
    },
    podNameMaxLength() {
      return Math.max(0, 191 - this.podExtension.length);
    },
    getPodVersions() {
      if (!this.pod) {
        return [];
      }

      const podStore = usePodStore();
      return podStore.getPodVersions(this.pod.id);
    },
    activeTenant() {
      const authStore = useAuthStore();
      return authStore.getActiveTenant;
    },
    showingUpgradeButton() {
      return  this.$canControlTenantConfig() &&
        !this.$isFCPExtension() &&
        !this.$isAdobeExtension() &&
        this.activeTenant &&
        this.activeTenant.subscription &&
        this.activeTenant.subscription.subscriptionType &&
        this.activeTenant.subscription.subscriptionType === 'freetrial';
    },
    getSubscriptionDaysLeft() {
      if (this.activeTenant?.subscription?.subscriptionEnd) {
        return this.activeTenant?.subscription?.subscriptionEnd?.days || 0;
      }

      return 0;
    },
    isQuickSwitcherVisible() {
      const podStore = usePodStore();
      return podStore.isQuickSwitcherVisible;
    },
    quickSwitcherPods() {
      const podStore = usePodStore();
      return podStore.getPods;
    },
  },
};
</script>

<style scoped>
.pod-main-logo {
  line-height: 0;
}

.pod-main-logo > img {
  width: 42px;
  height: 42px;
}

@media only screen and (max-width: 960px) {
  .pod-main-logo > img {
    width: 24px;
    height: 24px;
    margin-top: 3px;
  }
}

@media only screen and (max-width: 600px) {
  .pod-main-logo > img {
    width: 20px;
    height: 20px;
    margin-top: 6px;
  }
}

.pod-name-wrapper {
  max-width: 400px;
}

.top-pod-name {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px dashed transparent;
  transition: all 0.2s ease;
  cursor: pointer;
  user-select: none;
}

.top-pod-name:hover {
  border-color: rgba(128, 128, 128, 0.5);
}

.v-theme--dark .top-pod-name {
  color: #FFFFFF;
}

.v-theme--light .top-pod-name {
  color: #000000;
}

.edit-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.pod-name-hover:hover .edit-icon {
  opacity: 1;
}

.pod-name-input {
  max-width: 300px;
  min-width: 280px;
}

.pod-name-input :deep(.v-field__input) {
  padding: 8px 12px;
  min-height: auto;
}

.pod-name-input :deep(.v-field) {
  border: 1px dashed rgba(128, 128, 128, 0.5);
  border-radius: 4px;
}

.pod-name-input :deep(.v-field--error) {
  border-color: rgb(var(--v-theme-error));
}

.pod-name-extension {
  white-space: nowrap;
  pointer-events: none;
}
</style>
